/**
 * 逐骨旋转 gizmo（P0-6d 路线 B）：把 rotate TransformControls 附着到
 * 选中角色里 selectedBoneName 对应的那根骨骼，拖拽后反解写进 rig.boneOverrides。
 *
 * 关键约定（吸取机位 gizmo 连修三轮的教训）：
 *  · 拖拽期 TransformControls 独占 bone.quaternion，onObjectChange 只做「读当前四元
 *    → 反解 FBX 局部欧拉 → 存 override」，**不调 renderRig**（否则 store 回写会与拖拽
 *    互打架→跳变）。存下的 override 与骨头当前朝向自洽，之后任何 renderRig 都会复现同值。
 *  · 反解用 boneQuatToOverride，与 applyBoneMap 正向严格互逆（D-36a 同源坐标系教训）。
 *  · 与对象/机位 gizmo 互斥：selectedBoneName 非空时对象 Gizmo 让位（见 DirectorViewport）。
 */
import { useEffect, useState } from 'react';
import { TransformControls } from '@react-three/drei';
import { Euler, Quaternion } from 'three';
import type { Object3D } from 'three';

import { STD_FBX_REST } from '../../model/stdFbxRest';
import { boneQuatToOverride, withOverride } from './poseEdit';
import { getRuntime, subscribeRuntime } from './runtimeRegistry';
import { useDirectorStore } from './store';

function findBone(root: Object3D, name: string): Object3D | null {
  return root.getObjectByName(name) ?? null;
}

export function BoneGizmo() {
  const selObjId = useDirectorStore((s) => s.selectedObjectIds[0] ?? null);
  const boneName = useDirectorStore((s) => s.selectedBoneName);
  const rig = useDirectorStore(
    (s) => s.project.objects.find((o) => o.id === s.selectedObjectIds[0])?.rig,
  );
  const updateObject = useDirectorStore((s) => s.updateObject);
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeRuntime(() => setVersion((v) => v + 1)), []);

  void version; // 模型异步加载完成后重渲，确保能取到 bone 引用

  if (selObjId === null || boneName === null || rig === undefined) {
    return null;
  }
  const entry = getRuntime(selObjId);
  if (entry === null || entry.root === null) {
    return null;
  }
  const bone = findBone(entry.root, boneName);
  if (bone === null) {
    return null;
  }
  const id = selObjId;
  const name = boneName;
  const baseRig = rig;
  const rest = entry.rest;
  const b = bone;

  function commit(): void {
    const std = STD_FBX_REST[name];
    const r0 = rest.get(b);
    if (std === undefined || r0 === undefined) {
      return; // 无 std 或不在 rest 快照里的骨（如末端骨）不做反解，避免写错坐标系
    }
    const restQuat = new Quaternion().setFromEuler(new Euler(r0.x, r0.y, r0.z, r0.order as 'XYZ'));
    const euler = boneQuatToOverride(b.quaternion, restQuat, std);
    updateObject(id, { rig: withOverride(baseRig, name, euler) });
  }

  return (
    <TransformControls object={bone} mode="rotate" size={0.9} onObjectChange={commit} />
  );
}
